import { useParams, Link, useSearch } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrder, useVerifyPayment } from '../api/orders'
import { Heading, Text, Button, Container } from '@shinkansen/sakura'
import { PaymentInstructions } from '../components/PaymentInstructions'
import { getSafePaymentActionUrl } from '../utils/paymentUrls'
import styles from './tracking.module.css'

const STATUS_TIMELINE = [
  { key: 'PENDING', label: 'ORDER PLACED', description: 'Your order has been received' },
  { key: 'CONFIRMED', label: 'CONFIRMED', description: 'Payment received, order confirmed' },
  { key: 'PREPARING', label: 'PREPARING', description: 'Your eggs are being prepared' },
  { key: 'READY_FOR_PICKUP', label: 'READY', description: 'Ready for driver pickup' },
  { key: 'IN_TRANSIT', label: 'ON THE WAY', description: 'Driver is delivering your order' },
  { key: 'DELIVERED', label: 'DELIVERED', description: 'Order has been delivered' },
]

const PAYMENT_STATUS_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
  PENDING: {
    color: 'var(--color-status-warning)',
    bgColor: 'var(--color-status-warning-bg)',
    label: 'UNPAID',
  },
  PAID: {
    color: 'var(--color-status-success)',
    bgColor: 'var(--color-status-success-bg)',
    label: 'PAID',
  },
  EXPIRED: {
    color: 'var(--color-muted-foreground)',
    bgColor: 'var(--color-muted)',
    label: 'PAYMENT EXPIRED',
  },
  FAILED: {
    color: 'var(--color-status-danger)',
    bgColor: 'var(--color-status-danger-bg)',
    label: 'PAYMENT FAILED',
  },
  REFUNDED: {
    color: 'var(--color-status-info)',
    bgColor: 'var(--color-status-info-bg)',
    label: 'REFUNDED',
  },
}

const getStatusIndex = (status: string) => {
  const idx = STATUS_TIMELINE.findIndex(s => s.key === status)
  return idx >= 0 ? idx : 0
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('id-ID', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(price)
}

export function TrackingPage() {
  const { orderId } = useParams({ from: '/tracking/$orderId' })
  const search = useSearch({ from: '/tracking/$orderId' }) as { payment?: string }
  const queryClient = useQueryClient()
  const { data: order, isLoading, error, refetch } = useOrder(orderId)
  const verifyPayment = useVerifyPayment()
  const hasVerifiedRef = useRef(false)
  const isVerifying = search.payment === 'success' && verifyPayment.isPending

  // When redirected back from a provider action, verify once.
  useEffect(() => {
    if (search.payment === 'success' && orderId && !hasVerifiedRef.current) {
      hasVerifiedRef.current = true
      verifyPayment.mutate(orderId, {
        onSettled: () => {
          refetch()
        }
      })
    } else if (search.payment === 'failed' && !hasVerifiedRef.current) {
      hasVerifiedRef.current = true
      refetch()
      queryClient.invalidateQueries({ queryKey: ['myOrders'] })
    }
  }, [search.payment, orderId, verifyPayment, refetch, queryClient])

  // Poll for payment status when waiting (payment=pending)
  useEffect(() => {
    if (search.payment !== 'pending' || !orderId) return
    if (order?.paymentStatus && order.paymentStatus !== 'PENDING') return
    
    const pollInterval = setInterval(() => {
      verifyPayment.mutate(orderId, {
        onSuccess: (result) => {
          if (result && result.paymentStatus !== 'PENDING') {
            refetch()
            queryClient.invalidateQueries({ queryKey: ['myOrders'] })
          }
        }
      })
    }, 5000) // Poll every 5 seconds instead of 3
    
    return () => clearInterval(pollInterval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.payment, orderId, order?.paymentStatus])

  if (isLoading || isVerifying) {
    return (
      <div className={styles.page}>
        <Container size="md">
          <div className={styles.loading}>
            <div className={styles.loadingEgg} />
            <Text variant="muted">{isVerifying ? 'VERIFYING PAYMENT...' : 'LOADING ORDER...'}</Text>
          </div>
        </Container>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className={styles.page}>
        <Container size="md">
          <div className={styles.error}>
            <Heading size="lg">ORDER NOT FOUND</Heading>
            <Text variant="muted">The order you're looking for doesn't exist</Text>
            <Link to="/orders">
              <Button variant="primary">BACK TO ORDERS</Button>
            </Link>
          </div>
        </Container>
      </div>
    )
  }

  const currentStatusIndex = order.status === 'CANCELLED' ? -1 : getStatusIndex(order.status)
  const isCancelled = order.status === 'CANCELLED'
  const paymentActionUrl = getSafePaymentActionUrl(order.paymentUrl)

  return (
    <div className={styles.page}>
      <Container size="md">
        {/* Header */}
        <header className={styles.header}>
          <Link to="/orders" className={styles.backLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            BACK TO ORDERS
          </Link>
          
          <div className={styles.orderMeta}>
            <Text size="xs" variant="muted" className={styles.edition}>
              TASAMAGO • ORDER TRACKING
            </Text>
            <Heading level="h1" size="xl" className={styles.title}>
              {order.externalId || `#${order.id.slice(0, 8).toUpperCase()}`}
            </Heading>
          </div>
        </header>

        {/* Waiting for Payment (polling) */}
        {search.payment === 'pending' && order.paymentStatus === 'PENDING' && (
          <div className={styles.waitingPaymentBanner}>
            <div className={styles.waitingSpinner} />
            <div className={styles.waitingContent}>
              <span className={styles.waitingTitle}>WAITING FOR PAYMENT</span>
              <span className={styles.waitingDesc}>
                Use the payment instructions below. This page will update automatically.
              </span>
            </div>
            {paymentActionUrl && (
              <a href={paymentActionUrl} target="_blank" rel="noopener noreferrer" className={styles.waitingLink}>
                Open payment action
              </a>
            )}
          </div>
        )}

        {/* Payment Success Message */}
        {search.payment === 'success' && order.paymentStatus === 'PAID' && (
          <div className={styles.paymentSuccessBanner}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>Payment successful! Your order has been confirmed.</span>
          </div>
        )}

        {/* Payment Failed Message */}
        {search.payment === 'failed' && (
          <div className={styles.paymentFailedBanner}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <span>Payment was not completed. Please try again.</span>
          </div>
        )}

        {/* Payment Required Banner (not for cancelled orders) */}
        {order.status !== 'CANCELLED' && order.paymentStatus === 'PENDING' && (
          <div className={styles.paymentBanner}>
            <div className={styles.paymentBannerContent}>
              <div className={styles.paymentBannerIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div className={styles.paymentBannerText}>
                <span className={styles.paymentBannerTitle}>PAYMENT REQUIRED</span>
                <span className={styles.paymentBannerDesc}>Complete the selected direct payment to confirm the order</span>
              </div>
            </div>
            {paymentActionUrl && (
              <a
                href={paymentActionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.paymentBannerButton}
              >
                PAY NOW — {formatPrice(order.totalAmount + (order.deliveryFee || 0))}
              </a>
            )}
          </div>
        )}

        {order.status !== 'CANCELLED' && order.paymentStatus === 'PENDING' && (
          <PaymentInstructions order={order} />
        )}

        {/* Payment Status Badge (when not pending) */}
        {order.paymentStatus && order.paymentStatus !== 'PENDING' && (
          <div 
            className={styles.paymentStatusBadge}
            style={{
              backgroundColor: (PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PAID).bgColor,
              color: (PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PAID).color,
              borderColor: (PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PAID).color,
            }}
          >
            {(PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PAID).label}
          </div>
        )}

        {/* Current Status Banner */}
        <div 
          className={styles.statusBanner}
          data-status={order.status}
        >
          <div className={styles.statusIcon}>
            {isCancelled ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
            ) : currentStatusIndex === STATUS_TIMELINE.length - 1 ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            )}
          </div>
          <div className={styles.statusContent}>
            <span className={styles.statusLabel}>CURRENT STATUS</span>
            <span className={styles.statusValue}>
              {isCancelled ? 'CANCELLED' : STATUS_TIMELINE[currentStatusIndex]?.label}
            </span>
          </div>
        </div>

        {/* Order Details Card */}
        <div className={styles.detailsCard}>
          <div className={styles.detailsHeader}>
            <span className={styles.sectionNumber}>01</span>
            <span className={styles.sectionTitle}>ORDER DETAILS</span>
          </div>
          
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>DELIVERY ADDRESS</span>
              <span className={styles.detailValue}>{order.deliveryAddress}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>ORDER DATE</span>
              <span className={styles.detailValue}>{formatDate(order.createdAt)}</span>
            </div>
            {order.estimatedDelivery && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>ESTIMATED DELIVERY</span>
                <span className={styles.detailValue}>{formatDate(order.estimatedDelivery)}</span>
              </div>
            )}
            {order.deliveryFee > 0 && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>SUBTOTAL</span>
                <span className={styles.detailValue}>{formatPrice(order.totalAmount)}</span>
              </div>
            )}
            {order.deliveryFee > 0 && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>DELIVERY FEE</span>
                <span className={styles.detailValue}>{formatPrice(order.deliveryFee)}</span>
              </div>
            )}
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>TOTAL</span>
              <span className={styles.detailValueHighlight}>{formatPrice(order.totalAmount + (order.deliveryFee || 0))}</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        {!isCancelled && (
          <div className={styles.timelineCard}>
            <div className={styles.detailsHeader}>
              <span className={styles.sectionNumber}>02</span>
              <span className={styles.sectionTitle}>ORDER PROGRESS</span>
            </div>
            
            <div className={styles.timeline}>
              {STATUS_TIMELINE.map((step, index) => {
                const isCompleted = index < currentStatusIndex
                const isCurrent = index === currentStatusIndex
                const isPending = index > currentStatusIndex
                
                return (
                  <div 
                    key={step.key} 
                    className={styles.timelineItem}
                    data-completed={isCompleted}
                    data-current={isCurrent}
                    data-pending={isPending}
                  >
                    <div className={styles.timelineMarker}>
                      <div className={styles.timelineDot}>
                        {isCompleted && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                        {isCurrent && (
                          <div className={styles.currentPulse} />
                        )}
                      </div>
                      {index < STATUS_TIMELINE.length - 1 && (
                        <div className={styles.timelineLine} />
                      )}
                    </div>
                    <div className={styles.timelineContent}>
                      <span className={styles.timelineLabel}>{step.label}</span>
                      <span className={styles.timelineDesc}>{step.description}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Cancelled State */}
        {isCancelled && (
          <div className={styles.cancelledCard}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M15 9l-6 6M9 9l6 6"/>
            </svg>
            <Text size="lg">THIS ORDER HAS BEEN CANCELLED</Text>
            <Link to="/order/new">
              <Button variant="primary">PLACE NEW ORDER</Button>
            </Link>
          </div>
        )}
      </Container>
    </div>
  )
}
